import React, { memo, useMemo, useRef, useCallback } from 'react'
import { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisH, faBolt, faPause, faLock, faExclamation } from '@fortawesome/free-solid-svg-icons'
import { Message } from 'primereact/message'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Link } from 'react-router-dom'

import { IconSuspendedEv } from '../icon-suspended-ev'
import { getConnectorState, ConnectorState } from '../get-connector-state'
import {
    Connector,
    TopLimitingFactor,
    TopLimitingFactorDto,
    useTroubleshootingGetErrorCodeTroubleshootingInfoQuery,
} from '../../common/redux/api/web-api'
import { ConnectorChargingInfo } from '../connector-charging-info'
import { getErrorcodeInstructions } from '../../common/utils/errorStatus'
import Text from '../../ui/Text'
import useText from '../../common/hooks/useText'
import { ErrorCode, ErrorCodeInstructions } from '../../common/enums/errorCodeEnum'

export type ConnectorStatusIconColor = 'yellow' | 'blue' | 'green' | 'red' | 'gray' | 'darkGreen'

export const SConnectorStatusIcon = styled.span<{ color?: ConnectorStatusIconColor; center?: boolean }>`
    font-size: 1.5em;
    color: ${({ color }) => {
        switch (color) {
            case 'yellow':
                return '#ffcc4c'
            case 'green':
                return '#00c400'
            case 'darkGreen':
                return '#006c5c'
            case 'blue':
                return '#2277dd'
            case 'red':
                return '#dd0000'
            case 'gray':
                return '#888888'
            default:
                return '#000000'
        }
    }};
`

export type ConnectorStatusProps = {
    connector: Connector | undefined
    errorCode?: string
    isOcpp: boolean | undefined
    labeled?: boolean | undefined
    className?: ClassName
    style?: React.CSSProperties
    showErrorStatus?: boolean
    productId?: number
    showConsumption?: boolean
    connectorTopLimitation?: TopLimitingFactorDto | null
}

function ConnectorStatus({
    connector,
    isOcpp,
    labeled,
    showErrorStatus,
    showConsumption,
    productId,
    connectorTopLimitation,
}: ConnectorStatusProps): React.ReactElement {
    const text = useText()
    const state = useMemo(() => connector && getConnectorState(connector, isOcpp), [connector, isOcpp])
    const toggleOverlay = useRef<OverlayPanel>(null)

    const getErrorcode = useCallback(
        (externalError?: string | null, errorCode?: string | null) => {
            switch (externalError) {
                case ErrorCode.ConnectorLockFailure:
                    return text('errorConnectorLockFailure')

                case ErrorCode.GroundFailure:
                    return text('errorGroundFailure')

                case ErrorCode.HighTemperature:
                    return text('errorHighTemperature')

                case ErrorCode.OverCurrentFailure:
                    return text('errorOverCurrentFailure')

                case ErrorCode.PowerMeterFailure:
                    return text('errorPowerMeterFailure')

                case ErrorCode.UnderVoltage:
                    return text('errorUnderVoltage')

                case ErrorCode.EvSignalingError:
                    return text('errorEvSignalingError')

                case ErrorCode.InternalError:
                    return text('errorInternalError')

                default:
                    return (externalError ? externalError : errorCode) ?? undefined
            }
        },
        [text]
    )

    const { data: errorCodeInfo } = useTroubleshootingGetErrorCodeTroubleshootingInfoQuery(
        {
            productId: productId,
            errorCode: connector?.errorCode ?? '',
        },
        { skip: !isOcpp || connector?.errorCode === 'NoError' }
    )

    const showErrorStatusIcon = connector?.errorCode !== 'NoError' && !showErrorStatus && errorCodeInfo

    const showErrorInfo = showErrorStatus && connector?.errorCode !== 'NoError' && errorCodeInfo
    const showChargingLimitation = !showErrorInfo && connectorTopLimitation

    const userInstructions = errorCodeInfo?.instructions?.map((instruction, index) => {
        return (
            <span key={`${instruction}-${index}`} className="flex flex-column gap-4 p-1 pl-2">
                {getErrorcodeInstructions(instruction as ErrorCodeInstructions)}
            </span>
        )
    })

    const icon = useMemo(() => {
        switch (state) {
            case ConnectorState.AVAILABLE:
                return (
                    <>
                        <i
                            className="pi pi-check text-green-300 font-bold"
                            title={text('lblConnectorAvailableStatus')}
                            data-testid={`status-available-${connector?.name}`}
                        ></i>
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo.externalErrorCode, connector?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.PREPARING:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faEllipsisH}
                            color="#23c421"
                            title={text('lblConnectorPreparingStatus')}
                            data-testid="status-preparing"
                            aria-labelledby="color"
                            titleId="icon"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.CONNECTED:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faEllipsisH}
                            color="#23c421"
                            title={text('lblConnectorConnectedToEVStatus')}
                            data-testid="status-connected"
                            aria-labelledby="color"
                            titleId="icon"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.CHARGING:
                return showConsumption ? (
                    <ConnectorChargingInfo connector={connector} />
                ) : (
                    <>
                        <FontAwesomeIcon
                            icon={faBolt}
                            color="#2277dd"
                            title={text('lblConnectorChargingStatus')}
                            data-testid="status-charging"
                            className="mt-1 pointer"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.PAUSED_BY_CHARGER:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faPause}
                            color="#2277dd"
                            title={text('lblConnectorPausedByChargerStatus')}
                            data-testid="pausedByCharger-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.PAUSED_BY_VEHICLE:
                return (
                    <span title={text('lblConnectorPausedByVehicleStatus')}>
                        <IconSuspendedEv width={23} height={12} />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </span>
                )
            case ConnectorState.FINISHING:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faEllipsisH}
                            color="#888888"
                            title={text('lblConnectorFinishingStatus')}
                            data-testid="finishing-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.RESERVED:
                return (
                    <>
                        <FontAwesomeIcon
                            icon={faLock}
                            color="#888888"
                            title={text('lblConnectorReservedStatus')}
                            data-testid="reserved-status"
                        />
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.UNAVAILABLE:
                return (
                    <>
                        <i
                            className="pi pi-times text-gray-500 font-bold"
                            title={text('lblConnectorNotAvailableStatus')}
                        ></i>
                        {showErrorStatusIcon && (
                            <i
                                className="pi pi-exclamation-triangle text-yellow-500 pointer"
                                title={getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}
                            ></i>
                        )}
                    </>
                )
            case ConnectorState.FAULTED:
                return (
                    !showErrorStatus && (
                        <FontAwesomeIcon
                            icon={faExclamation}
                            color="#dd2222"
                            title={
                                connector?.errorCode && !showErrorStatus
                                    ? getErrorcode(connector?.errorCode)
                                    : text('lblConnectorErrorStatus')
                            }
                            data-testid="faulted-status"
                        />
                    )
                )

            default:
                return (
                    !showErrorStatus && (
                        <FontAwesomeIcon
                            icon={faExclamation}
                            className="pointer"
                            color="#dd2222"
                            title={
                                connector?.errorCode && !showErrorStatus
                                    ? getErrorcode(connector?.errorCode)
                                    : text('lblConnectorErrorStatus')
                            }
                            data-testid="fa-exclamation"
                        />
                    )
                )
        }
    }, [state, text, connector, showErrorStatusIcon, getErrorcode, errorCodeInfo, showConsumption, showErrorStatus])

    const getConnectorStatusLabel = (state?: ConnectorState): string | undefined => {
        return (
            {
                [ConnectorState.AVAILABLE]: text('lblConnectorAvailableStatus'),
                [ConnectorState.PREPARING]: text('lblConnectorPreparingStatus'),
                [ConnectorState.CHARGING]: text('lblConnectorChargingStatus'),
                [ConnectorState.CONNECTED]: text('lblConnectorConnectedToEVStatus'),
                [ConnectorState.FAULTED]: text('lblConnectorErrorStatus'),
                [ConnectorState.FINISHING]: text('lblConnectorFinishingStatus'),
                [ConnectorState.PAUSED_BY_CHARGER]: text('lblConnectorPausedByChargerStatus'),
                [ConnectorState.PAUSED_BY_VEHICLE]: text('lblConnectorPausedByVehicleStatus'),
                [ConnectorState.RESERVED]: text('lblConnectorReservedStatus'),
                [ConnectorState.UNAVAILABLE]: text('lblConnectorNotAvailableStatus'),
            } as { [key: number]: string }
        )[state ?? -1]
    }

    const getConnectorChargingLimitationLabel = (limitation: TopLimitingFactor): string => {
        const labels: Record<TopLimitingFactor, string> = {
            LoadbalancerCurrentLimitScheme: text('lblLoadbalancerCurrentLimitSchemeChargingLimitation'),
            MdOfflineFallback: text('lblMdOfflineFallbackChargingLimitation'),
            UserCurrentLimitZero: text('lblUserCurrentLimitZeroChargingLimitation'),
            Schedule: text('lblScheduleChargingLimitation'),
            AvailableCurrent: text('lblAvailableCurrentChargingLimitation'),
            GreenCharging: text('lblGreenChargingChargingLimitation'),
        }

        return labels[limitation]
    }

    const showLeftContent = (labeled && state !== undefined) || showChargingLimitation

    return (
        <>
            <div className="flex flex-row gap-2 w-12 text-md justify-content-between">
                {showLeftContent ? (
                    <div className="flex justify-content-center align-items-center gap-4">
                        {labeled && state !== undefined && <p className="mb-0">{getConnectorStatusLabel(state)}</p>}

                        {showChargingLimitation && (
                            <>
                                <Message
                                    data-testid="charging-limitation-message"
                                    severity={'warn'}
                                    content={
                                        <>
                                            <i className="pi pi-exclamation-triangle mr-1"></i>
                                            {getConnectorChargingLimitationLabel(connectorTopLimitation.factor!)}
                                        </>
                                    }
                                    className="flex align-items-center text-xs p-2 ml-1 sm:text-sm"
                                />
                            </>
                        )}
                    </div>
                ) : null}

                <div className="flex flex-row text-md align-items-center gap-4">
                    {showErrorInfo && (
                        <>
                            <Message
                                data-testid="error-status-message"
                                severity={
                                    connector?.ocppStatus === 'Faulted' && connector?.errorCode ? 'error' : 'warn'
                                }
                                content={
                                    <>
                                        <i className="pi pi-exclamation-triangle mr-1"></i>
                                        {getErrorcode(errorCodeInfo?.externalErrorCode, errorCodeInfo?.errorCode)}

                                        <i
                                            className="custom-target-icon-error pi pi-question-circle ml-1 pointer"
                                            data-pr-classname="col-3"
                                            onMouseEnter={(e) => toggleOverlay.current?.toggle(e)}
                                        ></i>
                                    </>
                                }
                                className="flex align-items-center text-xs p-2 ml-1 sm:text-sm"
                            />
                        </>
                    )}

                    {icon}
                </div>
            </div>

            <OverlayPanel ref={toggleOverlay} className="border-400 mr-5 sm:max-w-17rem">
                {userInstructions && userInstructions.length > 0 ? (
                    userInstructions
                ) : (
                    <span className="ml-2 mr-2 flex align-items-center gap-1">
                        <i className="pi pi-whatsapp"></i>
                        <Text code="lbllinkSupport" />

                        <Link
                            to={text('linkHelpCenter')}
                            target="_blank"
                            data-testid="go-to-support-page"
                            className="pi pi-external-link text-xs mt-1"
                        ></Link>
                    </span>
                )}
            </OverlayPanel>
        </>
    )
}

export default memo(ConnectorStatus)
